import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Badge from '../ui/badge'
import { InfoCircle } from '../../assets/icons'
import { Text1Bold, SubTitle } from '../ui/text'
import { Button } from '../ui/button'
import theme from '../../styled/theme'
import {
	Container,
	DonutWrapper,
	DonutPercentage,
	Content,
	BoldSpan,
	TextContainer,
	InfoPopupAnchor,
	InfoPopupOverlay,
	InfoPopup,
	InfoIconCircle
} from './styled'

const PRESENCE_INFO_TEXT =
	'Este indicador muestra el porcentaje de turnos a los que asististe y a los que no. Cancelar con anticipación cuando no puedas concurrir ayuda a que otros pacientes accedan a la atención que necesitan.'

const SIZE = 80
const STROKE_WIDTH = 8
const RADIUS = (SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const getPresenceTier = percentage => {
	if (percentage <= 20) {
		return {
			color: theme.colors.feedback.error,
			title: 'Tus inasistencias sin aviso afectan la disponibilidad de turnos.',
			subTitle: 'Si no pódes asistir, recordá cancelar tu turno con anticipación para que otro paciente pueda aprovecharlo.'
		}
	}
	if (percentage < 70) {
		return {
			color: theme.colors.brand.yellow,
			title: 'Asistir con regularidad mejora la continuidad de la atención.'
		}
	}
	return {
		color: theme.colors.brand.measureGreen,
		title: 'Gracias por tu compromiso'
	}
}

const AttendanceCard = ({ attended, total, percentage }) => {
	const offset = CIRCUMFERENCE * (1 - percentage / 100)
	const { color: donutColor, title, subTitle } = getPresenceTier(percentage)
	const [showInfoPopup, setShowInfoPopup] = useState(false)

	return (
		<Container>
			<DonutWrapper>
				<svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
					<circle
						cx={SIZE / 2}
						cy={SIZE / 2}
						r={RADIUS}
						fill="none"
						stroke={theme.colors.greys.dividerGray}
						strokeWidth={STROKE_WIDTH}
					/>
					<circle
						cx={SIZE / 2}
						cy={SIZE / 2}
						r={RADIUS}
						fill="none"
						stroke={donutColor}
						strokeWidth={STROKE_WIDTH}
						strokeLinecap="round"
						strokeDasharray={CIRCUMFERENCE}
						strokeDashoffset={offset}
						transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
					/>
				</svg>
				<DonutPercentage>{percentage}%</DonutPercentage>
			</DonutWrapper>
			<Content>
				<InfoPopupAnchor $active={showInfoPopup}>
					<Badge
						color={theme.colors.brand.blueOmint}
						background={theme.colors.brand.backgroundBlue}
						onClick={() => setShowInfoPopup(true)}
						style={{ cursor: 'pointer' }}
					>
						Nivel de presentismo
						{showInfoPopup ? (
							<InfoIconCircle $background={theme.colors.brand.backgroundBlue}>
								<InfoCircle
									size={14}
									ringColor={theme.colors.brand.blueOmint}
									color={theme.colors.brand.blueOmint}
									dotColor={theme.colors.brand.blueOmint}
								/>
							</InfoIconCircle>
						) : (
							<InfoCircle size={16} color={theme.colors.brand.blueOmint} />
						)}
					</Badge>
					{showInfoPopup && (
						<>
							<InfoPopupOverlay onClick={() => setShowInfoPopup(false)} />
							<InfoPopup>
								<SubTitle color={theme.colors.text.secondaryText}>
									{PRESENCE_INFO_TEXT}
								</SubTitle>
								<Button
									label="Entendido"
									$variant="primary"
									style={{ maxWidth: '100%' }}
									onClick={() => setShowInfoPopup(false)}
								/>
							</InfoPopup>
						</>
					)}
				</InfoPopupAnchor>
				<TextContainer>
					<Text1Bold size="16px">{title}</Text1Bold>
					<SubTitle color={theme.colors.text.mutedText}>
						Te presentaste a <BoldSpan>{attended}</BoldSpan> de los{' '}
						<BoldSpan>{total}</BoldSpan> turnos que reservaste el último año.{' '}{subTitle}
					</SubTitle>
				</TextContainer>
			</Content>
		</Container>
	)
}

AttendanceCard.propTypes = {
	attended: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
	percentage: PropTypes.number.isRequired
}

export default AttendanceCard
